import NumberFlow from "@number-flow/react"
import { Flex, Space, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Button from "../../../../Components/Button"
import { useAnimatedNumber } from "../../../../hooks/useAnimatedNumber"
import { overviewEmptyData } from "../../utils/emptyData"
import HealthBanner from "../HealthBanner"
import HealthOverviewChart from "./OverviewChart"

const { Title, Text } = Typography

const ChargebackOverview = ({ filters, handleChangeFilter }) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const mainInfo = useSelector((state) => state.health.mainInfo)
  const overviewData = useSelector((state) => state.health.overview)
  const { overviewType } = useSelector((state) => state.health.filters)

  const { is_business_health_available } = mainInfo || {}
  const chartData = is_business_health_available ? overviewData : overviewEmptyData

  const animatedValue = useAnimatedNumber(
    overviewType === "rate" ? mainInfo?.["chargeback_rate"] : mainInfo?.["chargeback_count"]
  )

  return (
    <Flex gap={32} vertical>
      <Flex justify="space-between" align="start" gap={12} vertical={isMobile}>
        <Flex gap={4} vertical>
          <Flex gap={8}>
            <Title level={5} style={{ margin: 0, fontSize: 14 }}>
              {`Chargeback ${overviewType[0].toUpperCase() + overviewType.slice(1)}`}
            </Title>
            {is_business_health_available && (
              <Tag color="success" bordered={false}>
                {mainInfo?.["prevention_rate"]}%
              </Tag>
            )}
          </Flex>

          <Flex gap={12} align="center" wrap={isMobile}>
            <Text
              style={{
                fontSize: isMobile ? 20 : 24,
                fontWeight: 400,
                lineHeight: 1.4,
                margin: 0
              }}
            >
              {is_business_health_available && (
                <NumberFlow
                  value={animatedValue}
                  suffix={overviewType === "rate" ? "%" : ""}
                  format={
                    overviewType === "rate"
                      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      : { maximumFractionDigits: 0 }
                  }
                />
              )}
              {!is_business_health_available && "0.00%"}
            </Text>
            {is_business_health_available && (
              <Text type="secondary" strong>
                {overviewType === "rate"
                  ? `${mainInfo?.["chargeback_rate_without_us"]}%`
                  : mainInfo?.["chargeback_count_without_us"]}{" "}
                without us
              </Text>
            )}
          </Flex>
          {is_business_health_available && mainInfo?.comparison_summary && (
            <Text type="secondary" style={{ fontSize: 14 }}>
              {overviewType === "rate"
                ? `${mainInfo.comparison_summary?.chargeback_rate ?? 0}%`
                : (mainInfo.comparison_summary?.chargeback_count ?? 0)}{" "}
              {mainInfo?.comparison_period?.label || "previous period"}
            </Text>
          )}
        </Flex>

        <Flex gap={12} align="center">
          <Space.Compact size={"large"} style={{ width: isMobile ? "100%" : undefined }}>
            <Button
              className={cls("compact-btn", {
                active: overviewType === "rate"
              })}
              onClick={() => handleChangeFilter({ overviewType: "rate" })}
              style={{
                borderRadius: "12px 0 0 12px",
                minWidth: 66,
                width: isMobile ? "50%" : undefined
              }}
            >
              {t("Rate")}
            </Button>
            <Button
              className={cls("compact-btn", {
                active: overviewType === "count"
              })}
              onClick={() => handleChangeFilter({ overviewType: "count" })}
              style={{ minWidth: 66, width: isMobile ? "50%" : undefined }}
            >
              {t("Count")}
            </Button>
          </Space.Compact>
        </Flex>
      </Flex>

      <HealthOverviewChart overviewData={chartData} />
      <HealthBanner type="chart-banner" />
    </Flex>
  )
}

export default ChargebackOverview
